import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { BUSINESS, IMG, SERVICES } from "../data/site";
import { PageHero } from "../components/Sections";
import { Logo } from "../components/Logo";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "",
  company: "",
  phone: "",
  email: "",
  service: "",
  quantity: "",
  fulfillment: "",
  message: "",
  website: "",
};

const inputCls =
  "w-full px-4 py-3 text-sm bg-white border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-colors";

const Field = ({ label, children, testid }) => (
  <div>
    <label className="block font-mono2 text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-700 mb-2" data-testid={`${testid}-label`}>
      {label}
    </label>
    {children}
  </div>
);

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [tsToken, setTsToken] = useState(null);
  const [tsKey, setTsKey] = useState(0);
  const onTsToken = useCallback((token) => setTsToken(token), []);
  const tsEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const { t, lang } = useLanguage();
  const c = t.contact;

  useEffect(() => {
    const service = searchParams.get("service");
    if (service) setForm((f) => ({ ...f, service }));
  }, [searchParams]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote`, { ...form, lang, started_at: startedAt, turnstile_token: tsToken || "" });
      setSent(true);
      setForm(EMPTY_FORM);
      setTsToken(null);
      setTsKey((k) => k + 1);
      toast.success(c.toastSuccess);
    } catch {
      toast.error(c.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHero overline={c.overline} title={c.title} intro={c.intro} img={IMG.shelves} />

      <section className="bg-[#FDFBF7] grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 p-7 sm:p-10">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 mb-2" data-testid="quote-form-title">
                {c.formTitle}
              </h2>
              <p className="text-sm text-slate-600 mb-8">{c.formSub}</p>

              {sent && (
                <div className="mb-8 flex items-start gap-3 border border-amber-600/40 bg-amber-50 p-4" data-testid="quote-success-alert">
                  <CheckCircle2 className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-800">
                    {c.success}{" "}
                    <a href={BUSINESS.phoneHref} className="font-bold text-amber-700">{BUSINESS.phone}</a>.
                  </p>
                </div>
              )}

              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="quote-form">
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                />
                <Field label={c.fields.name} testid="quote-name">
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder={c.placeholders.name} data-testid="quote-name-input" />
                </Field>
                <Field label={c.fields.company} testid="quote-company">
                  <input value={form.company} onChange={set("company")} className={inputCls} placeholder={c.placeholders.company} data-testid="quote-company-input" />
                </Field>
                <Field label={c.fields.phone} testid="quote-phone">
                  <input required type="tel" pattern="[0-9+()\-\s.]{7,25}" title="Enter a valid phone number" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={c.placeholders.phone} data-testid="quote-phone-input" />
                </Field>
                <Field label={c.fields.email} testid="quote-email">
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={c.placeholders.email} data-testid="quote-email-input" />
                </Field>
                <Field label={c.fields.service} testid="quote-service">
                  <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="quote-service-select">
                    <option value="" disabled>{c.placeholders.selectService}</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.slug}>{t.services[s.slug].title}</option>
                    ))}
                    <option value="other">{c.other}</option>
                  </select>
                </Field>
                <Field label={c.fields.quantity} testid="quote-quantity">
                  <input value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder={c.placeholders.quantity} data-testid="quote-quantity-input" />
                </Field>
                <Field label={c.fields.fulfillment} testid="quote-fulfillment">
                  <select required value={form.fulfillment} onChange={set("fulfillment")} className={inputCls} data-testid="quote-fulfillment-select">
                    <option value="" disabled>{c.placeholders.selectFulfillment}</option>
                    <option value="pickup">{c.pickup}</option>
                    <option value="delivery">{c.delivery}</option>
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label={c.fields.message} testid="quote-message">
                    <textarea rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder={c.placeholders.message} data-testid="quote-message-textarea" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <TurnstileWidget key={tsKey} onToken={onTsToken} />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending || (tsEnabled && !tsToken)}
                    data-testid="quote-submit-button"
                    className="w-full sm:w-auto px-8 py-3.5 text-sm font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    {sending ? c.sending : <>{c.submit} <Send className="h-4 w-4" /></>}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Contact info */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-slate-950 text-white p-7 sm:p-8" data-testid="contact-info-card">
              <div className="flex items-center gap-4 mb-6">
                <Logo className="h-16 w-16" testid="contact-logo" />
                <h3 className="font-heading text-xl font-bold tracking-tight">Pallet Works Logistic</h3>
              </div>
              <ul className="space-y-5 text-sm">
                <li className="flex gap-3.5">
                  <MapPin className="h-5 w-5 text-amber-500 shrink-0" />
                  <span className="text-slate-300">{BUSINESS.address1}<br />{BUSINESS.address2}</span>
                </li>
                <li>
                  <a href={BUSINESS.phoneHref} className="flex gap-3.5 hover:text-amber-400 transition-colors" data-testid="contact-phone-link">
                    <Phone className="h-5 w-5 text-amber-500 shrink-0" />
                    <span className="text-slate-300">{BUSINESS.phone}</span>
                  </a>
                </li>
                <li>
                  <a href={BUSINESS.emailHref} className="flex gap-3.5 hover:text-amber-400 transition-colors break-all" data-testid="contact-email-link">
                    <Mail className="h-5 w-5 text-amber-500 shrink-0" />
                    <span className="text-slate-300">{BUSINESS.email}</span>
                  </a>
                </li>
              </ul>
              <div className="mt-7 pt-6 border-t border-slate-800">
                <p className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-amber-500 font-semibold mb-4">
                  <Clock className="h-4 w-4" /> {c.hoursLabel}
                </p>
                <ul className="space-y-2 text-sm">
                  {t.hours.map((h, i) => (
                    <li key={i} className="flex justify-between gap-4" data-testid={`hours-${i}`}>
                      <span className="text-slate-400">{h.days}</span>
                      <span className="text-slate-200 font-semibold text-right">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border border-slate-200 bg-white overflow-hidden" data-testid="map-embed">
              <iframe
                title="Pallet Works Logistic location map"
                src="https://www.google.com/maps?q=19084+Cajon+Blvd,+San+Bernardino,+CA+92407&output=embed"
                className="w-full h-72 border-0"
                loading="lazy"
              />
              <div className="px-5 py-3.5 text-xs text-slate-500 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-amber-600" /> {BUSINESS.address1}, {BUSINESS.address2}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Contact;
